import type { HomeHeroPreferences, Meta } from './index.ts';

export const DEFAULT_HOME_HERO: HomeHeroPreferences = {
  source: 'brand',
  maxCertification: 'teen',
  minScore: 0,
  rotate: false,
};
/** Use strict validation for writes/acknowledgements; the reader below deliberately
 * repairs older stored values, which must not turn a malformed save into success. */
export function validHomeHeroPreferences(value: unknown): value is HomeHeroPreferences {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (Object.keys(v).some(key => !['source', 'catalogKey', 'maxCertification', 'minScore', 'rotate'].includes(key))) return false;
  const catalog = v.catalogKey;
  return (v.source === 'brand' || v.source === 'catalog') &&
    (catalog === undefined || (typeof catalog === 'string' && catalog.length > 0 && catalog.length <= 600 && !/[\u0000-\u001f]/.test(catalog))) &&
    (v.source !== 'catalog' || typeof catalog === 'string' && catalog.length > 0) &&
    typeof v.maxCertification === 'string' && ['kids', 'family', 'teen', 'all'].includes(v.maxCertification) &&
    typeof v.minScore === 'number' && Number.isFinite(v.minScore) && v.minScore >= 0 && v.minScore <= 10 && typeof v.rotate === 'boolean';
}
export function sameHomeHeroPreferences(a: HomeHeroPreferences, b: HomeHeroPreferences): boolean {
  return a.source === b.source && a.catalogKey === b.catalogKey && a.maxCertification === b.maxCertification && a.minScore === b.minScore && a.rotate === b.rotate;
}
/** Explicit US film/TV certifications only. Scores, genres and arbitrary age
 * numbers are not certifications and must never make an unknown title eligible. */
const levels: Record<string, number> = {
  G: 0,
  'TV-Y': 0,
  'TV-Y7': 0,
  'TV-G': 0,
  PG: 1,
  'TV-PG': 1,
  'PG-13': 2,
  'TV-14': 2,
  R: 3,
  'TV-MA': 3,
  'NC-17': 4,
};
export function contentCertification(value: unknown): string | undefined {
  if (typeof value !== 'string') return;
  const rating = value.trim().toUpperCase();
  return Object.hasOwn(levels, rating) ? rating : undefined;
}
export function homeHeroPreferences(value: unknown): HomeHeroPreferences {
  if (!value || typeof value !== 'object') return { ...DEFAULT_HOME_HERO };
  const v = value as Record<string, unknown>;
  const catalogKey =
    typeof v.catalogKey === 'string' &&
    v.catalogKey.length <= 600 &&
    !Array.from(v.catalogKey).some((c) => c.charCodeAt(0) < 32)
      ? v.catalogKey
      : undefined;
  return {
    source: v.source === 'catalog' && catalogKey ? 'catalog' : 'brand',
    ...(catalogKey ? { catalogKey } : {}),
    maxCertification:
      typeof v.maxCertification === 'string' &&
      ['kids', 'family', 'teen', 'all'].includes(v.maxCertification)
        ? (v.maxCertification as HomeHeroPreferences['maxCertification'])
        : 'teen',
    minScore:
      typeof v.minScore === 'number' &&
      Number.isFinite(v.minScore) &&
      v.minScore >= 0 &&
      v.minScore <= 10
        ? v.minScore
        : 0,
    rotate: v.rotate === true,
  };
}
export function heroEligible(
  meta: Meta,
  preferences: HomeHeroPreferences,
): boolean {
  const certification = contentCertification(meta.contentRating);
  if (preferences.maxCertification !== 'all') {
    const limit = { kids: 0, family: 1, teen: 2 }[preferences.maxCertification];
    if (!certification || levels[certification] > limit) return false;
  }
  if (preferences.minScore > 0) {
    const score =
      typeof meta.imdbRating === 'string' &&
      /^\d{1,2}(\.\d+)?$/.test(meta.imdbRating.trim())
        ? Number(meta.imdbRating)
        : NaN;
    if (!Number.isFinite(score) || score < preferences.minScore || score > 10)
      return false;
  }
  return true;
}
